import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { motion } from 'framer-motion';
import { Disclosure } from '@headlessui/react';
import classNames from 'classnames';
import Title from '../../Atoms/Title';
import Text from '../../Atoms/Text';
import Icon, { IconProps } from '../../Atoms/Icon';

type AccordionElement = {
  num: number;
  title: string;
  content: string;
  icon?: IconProps;
};

export interface Props {
  elements: AccordionElement[];
  className?: string;
}

function AccordionHorizontal({
  elements,
  className
}: Props) {
  const isMobile = useMediaQuery({
    query: `(max-width: 1023px)`
  });

  const DURATION = 0.5;

  const variantsDesktop = {
    open: {
      opacity: 1,
      width: 'auto',
      height: 'auto',
      transition: {
        duration: DURATION
      }
    },
    closed: {
      opacity: 0,
      height: 'auto',
      width: 0,
      transition: {
        duration: DURATION
      }
    }
  };

  const variantsMobile = {
    open: {
      opacity: 1,
      width: 'auto',
      height: 'auto'
    },
    closed: {
      opacity: 0,
      width: 'auto',
      height: 0
    }
  };

  const [isAnimating, setIsAnimating] = useState(false);
  const [disclosureState, setDisclosureState] = useState(1);
  function handleDisclosureChange(state: number) {
    if (state !== disclosureState) {
      setIsAnimating(true);
      setDisclosureState(state);

      setTimeout(() => {
        setIsAnimating(false);
      }, 100);
    }
  }

  return (
    <div
      className={classNames([
        'flex flex-col lg:flex-row',
        className
      ])}
    >
      {elements.map(({ num, title, content, icon }) => (
        <Disclosure key={num}>
          <div className="relative flex w-full flex-col border-t-2 border-sl-gray lg:h-[560px] lg:flex-row lg:border-t-0 lg:border-r-2 lg:first:border-l-2">
            <Disclosure.Button
              onClick={() => handleDisclosureChange(num)}
              onMouseEnter={() =>
                !isAnimating && handleDisclosureChange(num)
              }
              className={classNames([
                'relative flex h-full w-full flex-row items-center justify-between overflow-hidden py-5 lg:block lg:max-w-[560px] lg:py-0',
                disclosureState === num && 'py-0 lg:max-w-0'
              ])}
            >
              {disclosureState !== num && (
                <>
                  <div className="text-2xl font-bold text-sl-blue lg:absolute lg:-top-5 lg:left-full lg:-mr-5 lg:origin-bottom-left lg:-rotate-90 lg:transform lg:whitespace-nowrap lg:pb-5">
                    {num.toString().padStart(2, '0')}
                  </div>
                  <div className="text-lg font-bold text-sl-blue lg:absolute lg:bottom-0 lg:left-full lg:origin-bottom-left lg:-rotate-90 lg:transform lg:whitespace-nowrap lg:pb-5">
                    {title}
                  </div>
                </>
              )}
            </Disclosure.Button>
            <motion.div
              variants={
                !isMobile ? variantsDesktop : variantsMobile
              }
              animate={
                disclosureState === num ? 'open' : 'closed'
              }
              className="w-full overflow-hidden lg:w-[560px] lg:max-w-[560px]"
            >
              <div className="px-0 py-5 lg:w-[560px] lg:py-0 lg:pl-16 lg:pr-7 xl:pl-20">
                <div className="relative flex flex-col gap-5">
                  <span className="flex w-full flex-row items-center justify-between font-circular text-8xl font-bold text-sl-blue">
                    {num.toString().padStart(2, '0')}

                    {icon && isMobile && (
                      <Icon
                        icon={icon}
                        className="text-4xl text-sl-blue"
                      />
                    )}
                  </span>
                  <Title as="h3">
                    <span>{title}</span>
                  </Title>
                  <Text size="sm" className="">
                    {content}
                  </Text>
                  {icon && !isMobile && (
                    <Icon
                      icon={icon}
                      className="mr-auto text-sl-blue md:text-7xl lg:text-8xl"
                    />
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </Disclosure>
      ))}
    </div>
  );
}

export default AccordionHorizontal;
